import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IVF_STEPS, IVF_STEP_PREP, COMMUNITIES, LIVE_CHAT_ROOMS, DOCTORS, FERTILITY_ARTICLES } from '../data';
import { TTC_MAYA_CYCLE } from '../data/ttcMyCycleMock';
import { btnPrimary, btnGhost, demoChapterStackColumnStyle, DEMO_SECTION_STACK_GAP_PX, FERTILITY_DEMO_SECTION_BORDER } from '../designSystem';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import ChapterShellPageHeader from '../components/ChapterShellPageHeader';
import { getPageHeaderProps, CHAPTER_DISPLAY_NAMES, FERTILITY_DEMO_STATUS_PILL_TEXT } from '../data/pageHeaderConfig';
import { DEMO_FERTILITY_IVF_TIMELINE, formatIvfTimelineCallout } from '../utils/ivfCycleTimeline';
import { useApp } from '../context/AppContext';
import FertilityDemoHome from './FertilityDemoHome';
import FertilityDemoMedications from './FertilityDemoMedications';
import FertilityDemoDoctors from './FertilityDemoDoctors';
import FertilityDemoResources from './FertilityDemoResources';
import FertilityDemoGuides from './FertilityDemoGuides';
import FertilityDemoEvents from './FertilityDemoEvents';
import FertilityDemoLoss from './FertilityDemoLoss';
import FertilityDemoLGBTQ from './FertilityDemoLGBTQ';
import FertilityDemoFinancial from './FertilityDemoFinancial';
import FertilityDemoHealth from './FertilityDemoHealth';
import FertilityDemoAppointmentPrep from './FertilityDemoAppointmentPrep';
import FertilityDemoWhenToSeeDoctor from './FertilityDemoWhenToSeeDoctor';
import FertilityDemoProducts from './FertilityDemoProducts';
import TTCMyJourney from './ttc/TTCMyJourney';
import { TTCHealthRecord, TTCCommunity, TTCConnect, TTCEvents, TTCProducts } from './ttc/TTCPages';
import { PregnancyMyJourney, PregnancyHealthRecord, PregnancyCommunity, PregnancyConnect, PregnancyEvents, PregnancyProducts } from './pregnancy/PregnancyPages';
import { MotherhoodMyJourney, MotherhoodHealthRecord, MotherhoodCommunity, MotherhoodConnect, MotherhoodEvents, MotherhoodProducts } from './motherhood/MotherhoodPages';
import { MenopauseMyJourney, MenopauseHealthRecord, MenopauseCommunity, MenopauseConnect, MenopauseEvents, MenopauseProducts } from './menopause/MenopausePages';
import { FirstChapterMyJourney, FirstChapterHealthRecord, FirstChapterCommunity, FirstChapterConnect, FirstChapterEvents, FirstChapterProducts } from './first-chapter/FirstChapterPages';
import LearnAboutMyBody from './first-chapter/LearnAboutMyBody';
import MyChapter from './first-chapter/MyChapter';
import { TTCHomePage, PregnancyHomePage, MotherhoodHomePage, MenopauseHomePage, FirstChapterHomePage } from './home/ChapterHomePages';
import MilestoneSupport from './motherhood/MilestoneSupport';
import WorkingMother from './motherhood/WorkingMother';
import PreparingForLeave from './pregnancy/PreparingForLeave';
import CycleTracker from './demo/cycle/CycleTracker';
import TTCMyCyclePage from './ttc/TTCMyCyclePage';
import { NovaUnifiedPageBar, novaContextPillFromRaw } from '../components/NovaUnified';

/** Generic home content for non-fertility chapter demos (Nova, stats, share, communities). */
export function GenericChapterHome({
  palette,
  m,
  navigate,
  page,
  stageInfo,
  novaMessage,
  suggestionChips,
  stats,
  chapterCommunities,
  novaContext,
}) {
  const accent = palette.accent;
  return (
    <>
      <div style={{ marginBottom: DEMO_SECTION_STACK_GAP_PX }}>
        <NovaUnifiedPageBar
          layout="stacked"
          contextPill={novaContextPillFromRaw(novaContext ?? 'guidance')}
          body={novaMessage}
          actionLabel="Open Full Chat With Nova"
          onAction={() => navigate('/nova')}
          extraBelow={
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {(suggestionChips || []).map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => navigate('/nova')}
                    style={{
                      fontSize: 12,
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1px solid ${accent}88`,
                      background: 'var(--white)',
                      color: accent,
                      cursor: 'pointer',
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                    }}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </>
          }
        />
      </div>

      <div
        className="demo-shell-stack-grid"
        style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: DEMO_SECTION_STACK_GAP_PX }}
      >
        {(stats || []).map((s) => (
          <div
            key={s.label}
            style={{
              background: 'var(--card)',
              border: `1px solid ${palette.accent}30`,
              borderLeft: `4px solid ${palette.accent}`,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          background: 'var(--card)',
          border: `1px solid ${palette.accent}30`,
          borderRadius: 12,
          padding: 16,
          marginBottom: DEMO_SECTION_STACK_GAP_PX,
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <img
            src="/images/chapters/fertility.png"
            alt="Maya"
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
          <input
            type="text"
            placeholder="Share something with your chapter..."
            readOnly
            onClick={() => navigate('/community')}
            style={{
              flex: 1,
              padding: '10px 14px',
              border: `1px solid ${palette.accent}40`,
              borderRadius: 10,
              fontSize: 13,
              color: 'var(--text)',
              background: palette.bg,
              cursor: 'pointer',
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={{ fontSize: 12, padding: '6px 10px', border: `1px solid ${palette.accent}50`, borderRadius: 8, background: palette.bg, color: palette.accent, cursor: 'pointer' }}>📷 Photo</button>
          <button type="button" style={{ fontSize: 12, padding: '6px 10px', border: `1px solid ${palette.accent}50`, borderRadius: 8, background: palette.bg, color: palette.accent, cursor: 'pointer' }}>Post</button>
          <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: palette.bg, color: palette.accent, marginLeft: 'auto', fontWeight: 600 }}>{stageInfo?.name || page?.name}</span>
        </div>
      </div>

      <div>
        <h3 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)', marginBottom: 12 }}>Your {page?.name || 'chapter'} communities</h3>
        <div style={{ background: 'var(--card)', border: `1px solid ${palette.accent}30`, borderRadius: 12, overflow: 'hidden' }}>
          {(chapterCommunities || []).map((c, idx) => (
            <div
              key={c.title || idx}
              onClick={() => navigate('/community')}
              style={{
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                borderBottom: idx < (chapterCommunities?.length || 0) - 1 ? `1px solid ${palette.accent}20` : 'none',
                cursor: 'pointer',
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: palette.bgDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{c.emoji || '🌙'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.title}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.desc} · {c.members?.toLocaleString() || '—'} members</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: palette.bg, color: palette.accent }}>{c.tag || page?.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

function TtcDemoComingSoon({ palette }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slug = pathname.match(/^\/demo\/([^/]+)/)?.[1] || 'trying-to-conceive';
  return (
    <div style={{ background: 'var(--white)', border: `1px solid ${palette.accent}30`, borderRadius: 14, padding: 28, textAlign: 'center' }}>
      <p style={{ fontFamily: FONT_SERIF, fontSize: 22, color: 'var(--text)', marginBottom: 8 }}>Coming soon</p>
      <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 16px' }}>
        We&apos;re building this for you — check back shortly.
      </p>
      <button
        type="button"
        onClick={() => navigate(`/demo/${slug}/home`)}
        style={{
          fontFamily: FONT_BODY,
          fontSize: 12,
          fontWeight: 600,
          color: palette.accent,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        ← Back to hub
      </button>
    </div>
  );
}

const MOCK_WOMEN = [
  { initials: 'JM', name: 'Jasmine M.', detail: 'IVF · Cycle 2 · Columbus, OH' },
  { initials: 'NR', name: 'Noelle R.', detail: 'Stims · Day 8 · Cleveland' },
  { initials: 'AK', name: 'Ava K.', detail: 'Transfer prep · Cincinnati' },
];

const MOCK_FEED = [
  { user: 'Sarah K.', content: 'Retrieval done! 12 eggs retrieved 🎉', time: '2h ago', reactions: 24 },
  { user: 'Maya T.', content: 'Anyone else’s clinic doing stim day 8 scans tomorrow? Nervous but ready.', time: '5h ago', reactions: 12 },
  { user: 'Priya R.', content: 'Just got my fertilization report — 8 fertilized. Cautiously optimistic 💕', time: '1d ago', reactions: 31 },
];

const FERTILITY_RESOURCES = [
  { title: 'Understanding your AMH', desc: 'What it means and what to ask', tag: '5 min' },
  { title: 'Egg retrieval prep checklist', desc: 'What to bring, what to expect', tag: 'Guide' },
  { title: 'Managing stim side effects', desc: 'When to call the clinic', tag: '3 min' },
];

const FERTILITY_GROUPS = COMMUNITIES.filter((c) => (c.chapters || []).includes('ivf')).slice(0, 4);

export { FertilityDemoHome };

const FERTILITY_PALETTE = { primary: 'var(--pink)', primaryL: 'var(--pink-l)', primaryM: 'var(--pink-m)', primaryDk: 'var(--pink-d)' };

export function FertilityDemoJourney({ palette, m, navigate, omitHeader }) {
  const accent = palette.accent;
  const bg = palette.bg;

  const mockCycles = [
    {
      id: 1,
      name: 'Cycle 1',
      startedAt: '2025-02-15',
      status: 'In progress',
      step: 'Stims',
      medications: [
        { name: 'Gonal-F', dose: '225', units: 'IU', schedule: 'Nightly 8pm', days: '1–10' },
        { name: 'Menopur', dose: '75', units: 'IU', schedule: 'Nightly 8pm', days: '1–10' },
      ],
    },
  ];

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: DEMO_SECTION_STACK_GAP_PX,
      }}
    >
      {!omitHeader && (
        <ChapterShellPageHeader
          pageTitle="Your IVF story"
          chapter="Fertility"
          headerChapter="fertility"
          eyebrow="Fertility · My Journey"
          subtitle="Cycles, stims, and milestones — Nova uses this to personalize your support."
          statusPill={FERTILITY_DEMO_STATUS_PILL_TEXT}
          statusCard={{
            eyebrow: 'IVF',
            title: '',
            subtitle: '',
            pills: [
              { label: 'Retrieval ~9 days away', dot: true, dotColor: 'bg-rose-400' },
              { label: 'Injections at 9pm tonight' },
              { label: '12 follicles last scan' },
            ],
            stat: { value: '~9', label: 'days to retrieval' },
            statCallout: formatIvfTimelineCallout(DEMO_FERTILITY_IVF_TIMELINE),
          }}
          novaMessage={{
            context: 'afternoon check-in',
            message: "How are you feeling after this morning? Drink plenty of water today. Your injections are at 9pm tonight.",
          }}
          onSignUp={(p) => navigate(p)}
          palette={FERTILITY_PALETTE}
        />
      )}
      <div
        style={{
          background: 'var(--card)',
          border: FERTILITY_DEMO_SECTION_BORDER,
          borderRadius: 14,
          padding: 20,
        }}
      >
        <SectionHeader
          title="Your IVF protocol"
          descriptor="Add each cycle with start date, then log medications and when to take them. You'll get reminders so nothing is missed."
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16 }}>
          <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, background: accent, color: 'white', border: 'none' }}>
            Start new cycle
          </button>
        </div>

        {mockCycles.map((cycle) => (
          <div
            key={cycle.id}
            style={{
              background: bg,
              border: FERTILITY_DEMO_SECTION_BORDER,
              borderRadius: 12,
              padding: 18,
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{cycle.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Started {cycle.startedAt} · {cycle.step}
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--sage-l)',
                  color: 'var(--sage-d)',
                }}
              >
                {cycle.status}
              </span>
            </div>

            <div style={{ marginBottom: 12 }}>
              <SectionHeader title="Medications" descriptor="Doses and schedules for this cycle — reminders stay with each med." />
            </div>
            {cycle.medications.map((med, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  background: 'var(--white)',
                  borderRadius: 8,
                  marginBottom: 8,
                  border: '1px solid var(--border)',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {med.name} {med.dose} {med.units}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {med.schedule} · Days {med.days}
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: accent }}>Reminders on</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Journey-specific content for non-fertility chapters (timeline, tracking, etc.). */
function GenericChapterJourney({ palette, m, navigate, page, stageInfo }) {
  const accent = palette.accent;
  const bg = palette.bg;
  const journeyLabels = {
    cycle: 'Your cycle tracking',
    ttc: 'Your TTC journey',
    pregnancy: 'Your pregnancy timeline',
    postpartum: 'Your motherhood journey',
    menopause: 'Your menopause tracking',
  };
  const title = journeyLabels[page?.id] || `Your ${page?.name || 'chapter'} journey`;
  return (
    <div style={{ background: 'var(--card)', border: `1px solid ${palette.accent}30`, borderRadius: 14, padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <SectionHeader
          title={title}
          descriptor="Track your story, milestones, and progress. Nova and your community are here to support you."
        />
      </div>
      <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, background: accent }}>
        Start free with Nova
      </button>
    </div>
  );
}

const FULL_PAGE_SECTIONS = [
  'myjourney',
  'journey',
  'cycle',
  'cycle-intelligence',
  'cycle-school',
  'appointment-prep',
  'when-to-see-a-doctor',
  'partner-support',
  'lgbtq',
  'health',
  'community',
  'connect',
  'events',
  'products',
  'milestones',
  'working',
  'leave',
  'baby',
  'wellbeing',
  'activities',
  'opk',
  'tww',
  'learn',
  'mom',
  'doctor',
  'doctors',
  'birthplan',
  'hrt',
  'doctorprep',
  'ppd',
  'loss',
  'loss-and-recovery',
  'sleep',
  'feeding',
  'resources',
];
const FULL_PAGE_CHAPTERS = ['ttc', 'trying-to-conceive', 'pregnancy', 'postpartum', 'menopause', 'cycle', 'motherhood', 'first-chapter'];

function resolveChapter(chapter) {
  if (chapter === 'first-chapter') return 'cycle';
  if (chapter === 'motherhood') return 'postpartum';
  return chapter;
}

/** Short label for demo page eyebrows (community/connect fallback). */
function demoChapterEyebrowLabel(chapter) {
  if (chapter === 'ttc' || chapter === 'trying-to-conceive') return 'TTC';
  if (chapter === 'motherhood') return CHAPTER_DISPLAY_NAMES.motherhood;
  const r = resolveChapter(chapter);
  if (r === 'postpartum') return CHAPTER_DISPLAY_NAMES.motherhood;
  if (r === 'cycle') return 'First Chapter';
  if (r === 'pregnancy') return CHAPTER_DISPLAY_NAMES.pregnancy;
  if (r === 'menopause') return CHAPTER_DISPLAY_NAMES.menopause;
  if (r === 'fertility') return CHAPTER_DISPLAY_NAMES.fertility;
  return CHAPTER_DISPLAY_NAMES[r] || 'Chapter';
}

function getFullPageComponent(chapter, section, navigate, palette) {
  const ch = chapter === 'trying-to-conceive' ? 'ttc' : chapter;
  const sect = section === 'journey' ? 'myjourney' : section;
  const ttcSoon = () => <TtcDemoComingSoon palette={palette} />;
  const postpartumPages = {
    myjourney: MotherhoodMyJourney, health: MotherhoodHealthRecord, community: MotherhoodCommunity,
    connect: MotherhoodConnect, events: MotherhoodEvents, products: MotherhoodProducts,
    milestones: MilestoneSupport, working: WorkingMother, baby: MotherhoodMyJourney,
    wellbeing: MotherhoodMyJourney, activities: MotherhoodMyJourney, ppd: MotherhoodCommunity,
    sleep: MotherhoodMyJourney, feeding: MotherhoodMyJourney,
  };
  const pregnancyPages = {
    myjourney: PregnancyMyJourney, health: PregnancyHealthRecord, community: PregnancyCommunity,
    connect: PregnancyConnect, events: PregnancyEvents, products: PregnancyProducts,
    leave: PreparingForLeave, birthplan: PregnancyMyJourney, doctor: PregnancyMyJourney,
  };
  const FullPages = {
    ttc: {
      myjourney: TTCMyJourney,
      cycle: TTCMyCyclePage,
      health: TTCHealthRecord,
      community: TTCCommunity,
      connect: TTCConnect,
      events: TTCEvents,
      products: TTCProducts,
      opk: TTCMyJourney,
      tww: TTCMyJourney,
      doctor: TTCHealthRecord,
      'cycle-intelligence': ttcSoon,
      'cycle-school': ttcSoon,
      'appointment-prep': ttcSoon,
      'when-to-see-a-doctor': ttcSoon,
      'partner-support': ttcSoon,
      doctors: ttcSoon,
      'loss-and-recovery': ttcSoon,
    },
    pregnancy: pregnancyPages,
    postpartum: postpartumPages,
    motherhood: postpartumPages,
    menopause: { myjourney: MenopauseMyJourney, health: MenopauseHealthRecord, community: MenopauseCommunity, connect: MenopauseConnect, events: MenopauseEvents, products: MenopauseProducts, hrt: MenopauseMyJourney, doctorprep: MenopauseMyJourney, doctor: MenopauseHealthRecord },
    cycle: { myjourney: CycleTracker, tracker: CycleTracker, health: FirstChapterHealthRecord, community: FirstChapterCommunity, connect: FirstChapterConnect, events: FirstChapterEvents, products: FirstChapterProducts, learn: LearnAboutMyBody, mom: MyChapter, doctor: CycleTracker },
    'first-chapter': { myjourney: CycleTracker, tracker: CycleTracker, health: FirstChapterHealthRecord, community: FirstChapterCommunity, connect: FirstChapterConnect, events: FirstChapterEvents, products: FirstChapterProducts, learn: LearnAboutMyBody, mom: MyChapter, doctor: CycleTracker },
  };
  const resolved = resolveChapter(ch);
  const Comp = FullPages[ch]?.[sect] || FullPages[resolved]?.[sect];
  return Comp ? <Comp navigate={navigate} /> : null;
}

export function FertilityDemoSection({ section, palette, m, navigate, page, stageInfo, chapter, demoStats }) {
  const isFertility = chapter === 'fertility';
  const isHome = section === 'home' || !section || section === '';
  const isJourney = section === 'journey' || section === 'myjourney';
  const stats = demoStats || [];
  const genericHomeProps = {
    palette,
    m,
    navigate,
    page,
    stageInfo,
    novaMessage: page?.nova_intro || "I'm here whenever you need me. How are you feeling today?",
    suggestionChips: page?.suggestions?.slice(0, 3) || ['How am I doing?', 'What should I ask my doctor?', 'Connect me with others'],
    stats,
    chapterCommunities: page?.communities?.slice(0, 3) || [],
    novaContext: page?.nova_context,
  };

  // Full page components for non-fertility chapters
  const sect = section || 'myjourney';
  const fullPage = getFullPageComponent(chapter, sect, navigate, palette);
  if (fullPage) {
    const headerProps = getPageHeaderProps(chapter, sect, navigate);
    const isTtcCycle = (chapter === 'ttc' || chapter === 'trying-to-conceive') && sect === 'cycle';
    if (isTtcCycle) {
      const hp = headerProps;
      const cycleDesc =
        hp?.subtitle || "Today's log, BBT chart, OPK results, and cycle pattern insights.";
      const pillCombined = `Cycle ${TTC_MAYA_CYCLE.cycleNumber} · Day ${TTC_MAYA_CYCLE.cycleDay}`;
      return (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader
            {...hp}
            pageTitle={`Cycle ${TTC_MAYA_CYCLE.cycleNumber}, Day ${TTC_MAYA_CYCLE.cycleDay}`}
            statusPillText={pillCombined}
            subtitle={cycleDesc}
            statusCard={
              hp?.statusCard
                ? {
                    ...hp.statusCard,
                    pills: [
                      { label: 'Day 14 · ovulatory', dot: true, dotColor: 'bg-orange-400' },
                      { label: 'OPK peak · BBT shift confirmed' },
                      { label: 'Fertile window day 2 of 3' },
                    ],
                  }
                : undefined
            }
            trailing={
              <>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--purple-dk)',
                    background: 'var(--purple-l)',
                    border: '1px solid var(--purple-m)',
                    borderRadius: 999,
                    padding: '5px 12px',
                  }}
                >
                  {TTC_MAYA_CYCLE.monthsTryingLabel}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#fff',
                    background: 'var(--purple)',
                    border: '1px solid var(--purple)',
                    borderRadius: 999,
                    padding: '5px 12px',
                  }}
                >
                  {TTC_MAYA_CYCLE.fertileBadgeLabel}
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/nova')}
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fff',
                    background: 'var(--purple)',
                    border: 'none',
                    borderRadius: 999,
                    padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                >
                  + Log today
                </button>
              </>
            }
          />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{fullPage}</div>
        </div>
      );
    }
    if (headerProps) {
      return (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...headerProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{fullPage}</div>
        </div>
      );
    }
    return <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{fullPage}</div>;
  }

  if (isFertility && section === 'lgbtq') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = <FertilityDemoLGBTQ m={m} navigate={navigate} />;
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }

  if (isFertility && section === 'partner-support') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = <TtcDemoComingSoon palette={palette} />;
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }

  if (isFertility && section === 'appointment-prep') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = <FertilityDemoAppointmentPrep />;
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }

  if (isFertility && section === 'when-to-see-a-doctor') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = <FertilityDemoWhenToSeeDoctor navigate={navigate} />;
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }

  if (isHome) {
    const homeHeaderProps = getPageHeaderProps(chapter, 'home', navigate);
    if (isFertility) {
      const content = <FertilityDemoHome palette={palette} m={m} navigate={navigate} page={page} stageInfo={stageInfo} omitHeader={!!homeHeaderProps} />;
      return homeHeaderProps ? (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...homeHeaderProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
        </div>
      ) : (
        <>{content}</>
      );
    }
    const homePages = {
      ttc: TTCHomePage,
      'trying-to-conceive': TTCHomePage,
      pregnancy: PregnancyHomePage,
      motherhood: MotherhoodHomePage,
      postpartum: MotherhoodHomePage,
      menopause: MenopauseHomePage,
      'first-chapter': FirstChapterHomePage,
      cycle: FirstChapterHomePage,
    };
    const HomeComp = homePages[chapter] || homePages[resolveChapter(chapter)];
    if (HomeComp) {
      const content = <HomeComp navigate={navigate} palette={palette} m={m} hideTopHeader />;
      return homeHeaderProps ? (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...homeHeaderProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
        </div>
      ) : (
        <>{content}</>
      );
    }
    const content = <GenericChapterHome {...genericHomeProps} />;
    return homeHeaderProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...homeHeaderProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      <>{content}</>
    );
  }
  if (isJourney) {
    const journeyContent = isFertility
      ? <FertilityDemoJourney palette={palette} m={m} navigate={navigate} omitHeader />
      : <GenericChapterJourney palette={palette} m={m} navigate={navigate} page={page} stageInfo={stageInfo} />;
    const headerProps = getPageHeaderProps(chapter, section || 'journey', navigate);
    if (headerProps) {
      return (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...headerProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{journeyContent}</div>
        </div>
      );
    }
    return journeyContent;
  }
  if (section === 'health') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = <FertilityDemoHealth navigate={navigate} omitHeader={!!headerProps} demoChapter={chapter} />;
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} hideNovaMessage />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }
  if (section === 'doctors') {
    if (isFertility) {
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      const content = <FertilityDemoDoctors navigate={navigate} omitHeader={!!headerProps} />;
      return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
    }
    return (
      <div style={{ background: 'var(--card)', border: `1px solid ${palette.accent}30`, borderRadius: 14, padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <SectionHeader
            title="My Doctors"
            descriptor="REIs and fertility specialists matched to your location and needs."
          />
        </div>
        <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 13, fontWeight: 700, color: 'white', background: palette.accent, border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}>Find & Add Doctors</button>
      </div>
    );
  }
  if (section === 'medications') {
    if (isFertility) {
      const content = <FertilityDemoMedications navigate={navigate} omitHeader />;
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
    }
    return (
      <div style={{ background: 'var(--card)', border: `1px solid ${palette.accent}30`, borderRadius: 14, padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <SectionHeader
            title="Medications"
            descriptor="Log your protocol, set reminder times, and track what you&apos;ve taken."
          />
        </div>
        <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 13, fontWeight: 700, color: 'white', background: palette.accent, border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}>Log your protocol & get reminders</button>
      </div>
    );
  }
  if (section === 'financial') {
    if (isFertility) {
      const content = <FertilityDemoFinancial navigate={navigate} omitHeader />;
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
    }
    return (
      <div style={{ background: 'var(--card)', border: `1px solid ${palette.accent}30`, borderRadius: 14, padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <SectionHeader
            title="Financial tracker"
            descriptor="Track spending, upload receipts, and estimate your total IVF costs."
          />
        </div>
        <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 13, fontWeight: 700, color: 'white', background: palette.accent, border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}>Start free to track your costs</button>
      </div>
    );
  }
  if (section === 'guides') {
    if (isFertility) {
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      const content = <FertilityDemoGuides navigate={navigate} omitHeader={!!headerProps} />;
      return headerProps ? (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...headerProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
        </div>
      ) : (
        content
      );
    }
    return null;
  }
  if (section === 'resources') {
    if (isFertility || chapter === 'trying-to-conceive' || chapter === 'ttc') {
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      const content = <FertilityDemoResources navigate={navigate} omitHeader={!!headerProps} demoChapter={chapter ?? 'fertility'} />;
      return headerProps ? (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...headerProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
        </div>
      ) : (
        content
      );
    }
    return (
      <div style={{ fontFamily: FONT_BODY }}>
        <h1 style={{ fontFamily: FONT_SERIF, fontSize: 22, color: 'var(--text)', marginBottom: 6 }}>Resources</h1>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 28 }}>Curated from credible sources across the internet.</p>
        <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 13, fontWeight: 700, color: 'white', background: palette.accent, border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}>Sign up to access</button>
      </div>
    );
  }
  if (section === 'products') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = (
      <FertilityDemoProducts navigate={navigate} m={m} demoChapter={chapter} omitNovaPicksBar={!!headerProps} />
    );
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }
  if (section === 'events') {
    if (isFertility) {
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      const content = <FertilityDemoEvents navigate={navigate} omitHeader={!!headerProps} demoChapter={chapter ?? 'fertility'} />;
      return headerProps ? (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...headerProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
        </div>
      ) : (
        content
      );
    }
    return <FertilityDemoEvents navigate={navigate} demoChapter={chapter ?? 'fertility'} />;
  }
  if (section === 'loss' || section === 'loss-and-recovery') {
    const headerProps = getPageHeaderProps(chapter, section, navigate);
    const content = <FertilityDemoLoss navigate={navigate} demoChapter={chapter ?? 'fertility'} />;
    return headerProps ? (
      <div style={demoChapterStackColumnStyle}>
        <ChapterShellPageHeader {...headerProps} />
        <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
      </div>
    ) : (
      content
    );
  }
  if (section === 'community' || section === 'connect') {
    if (isFertility) {
      const headerProps = getPageHeaderProps(chapter, section, navigate);
      const content = (
        <div style={{ fontFamily: FONT_BODY, textAlign: 'center', padding: '24px 24px 48px' }}>
          <p style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 400, margin: `0 auto ${DEMO_SECTION_STACK_GAP_PX}px`, lineHeight: 1.6 }}>
            Connect with women who share your journey. Sign up to access the full community.
          </p>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            style={{ fontSize: 13, fontWeight: 700, color: 'white', background: palette.accent, border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}
          >
            Sign up
          </button>
        </div>
      );
      return headerProps ? (
        <div style={demoChapterStackColumnStyle}>
          <ChapterShellPageHeader {...headerProps} />
          <div style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>{content}</div>
        </div>
      ) : (
        content
      );
    }
    const isTtcCh = chapter === 'ttc' || chapter === 'trying-to-conceive';
    const commTitle = section === 'community' ? 'Community' : 'Connect';
    return (
      <div style={{ fontFamily: FONT_BODY, textAlign: 'center', padding: '48px 24px' }}>
        <PageHeader
          chapter={isTtcCh ? 'ttc' : 'fertility'}
          eyebrow={`${demoChapterEyebrowLabel(chapter)} · ${commTitle}`}
          title={commTitle}
          descriptor="Connect with women who share your journey. Sign up to access the full community."
          trailing={
            <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, background: palette.accent }}>
              Sign up
            </button>
          }
        />
      </div>
    );
  }
  if (isFertility) {
    return <FertilityDemoHome palette={palette} m={m} navigate={navigate} page={page} stageInfo={stageInfo} />;
  }
  return <GenericChapterHome {...genericHomeProps} />;
}
